// One-Euro filter (Casiez, Roussel, Vogel — 2012).
//
// A low-pass filter where the cutoff frequency itself is adapted to the
// derivative of the signal. When the rider is steady the cutoff drops and
// the output is very smooth. When the rider moves (position change,
// breathing wobble going away, etc.) the cutoff rises and the output
// tracks the change with low lag.
//
// This replaces the fixed-α EmaSmoother + the explicit "raise α during
// transitions" hacks that would otherwise be needed in Track 1 (continuous
// Cd) and Track 3 (event triggers).
//
// Math:
//   α(cutoff) = 1 / (1 + 1/(2π·cutoff·dt))
//   dx_hat = lowpass(dx, α(d_cutoff))
//   cutoff_x = min_cutoff + beta·|dx_hat|
//   x_hat   = lowpass(x, α(cutoff_x))
//
// Recommended values from the paper for human-motion signals:
//   min_cutoff = 1.0  Hz  — base cutoff when stationary
//   beta       = 0.007    — how aggressively cutoff scales with speed
//   d_cutoff   = 1.0  Hz  — cutoff on the derivative estimator itself
//
// For CdA we want a noticeably calmer signal than the human-motion default
// so default min_cutoff = 0.5 and beta = 0.05 (tuned empirically against
// the existing EMA(α=0.18) — sits below it on steady-state noise and
// above it during transitions).

export interface OneEuroOptions {
  /** Base cutoff frequency when the signal isn't changing, Hz. Lower = smoother. */
  minCutoff?: number;
  /** Cutoff scaling factor — higher = more responsive during fast changes. */
  beta?: number;
  /** Cutoff frequency applied to the derivative estimator. */
  dCutoff?: number;
  /** Outlier sigma rejection (preserve from EmaSmoother). Frames where the
   *  raw reading is more than this many σ from the smoothed value are
   *  shrunk toward the smoothed value by 75% rather than fully applied. */
  outlierSigma?: number;
  /** When true, accept zero and negative input values. Default false keeps
   *  the existing guard that drops `raw <= 0` — useful for CdA/area/power
   *  which are physically positive. Pose landmarks have a hip-centered
   *  origin so X and Z are routinely negative; those callers must opt in. */
  acceptNegatives?: boolean;
}

function alpha(cutoffHz: number, dtSec: number): number {
  const tau = 1.0 / (2 * Math.PI * cutoffHz);
  return 1.0 / (1.0 + tau / dtSec);
}

export class OneEuroFilter {
  private readonly minCutoff: number;
  private readonly beta: number;
  private readonly dCutoff: number;
  private readonly outlierSigma: number;
  private readonly acceptNegatives: boolean;
  private xHat: number | null = null;
  private dxHat: number = 0;
  private lastT: number | null = null;
  private variance = 0;

  constructor(opts: OneEuroOptions = {}) {
    this.minCutoff = opts.minCutoff ?? 0.5;
    this.beta = opts.beta ?? 0.05;
    this.dCutoff = opts.dCutoff ?? 1.0;
    this.outlierSigma = opts.outlierSigma ?? 4;
    this.acceptNegatives = opts.acceptNegatives ?? false;
  }

  /**
   * Feed a new reading. Returns the smoothed value, or null until the first
   * reading. timestampMs is optional; if not provided, we assume the caller
   * is feeding at a roughly constant rate and use a 1/30s step.
   */
  update(raw: number, timestampMs?: number): number | null {
    if (!Number.isFinite(raw)) return this.xHat;
    if (!this.acceptNegatives && raw <= 0) return this.xHat;

    const tSec = (timestampMs ?? performance.now()) / 1000;

    if (this.xHat === null || this.lastT === null) {
      this.xHat = raw;
      this.dxHat = 0;
      this.lastT = tSec;
      this.variance = 0;
      return this.xHat;
    }

    const dt = Math.max(1e-3, tSec - this.lastT);
    this.lastT = tSec;

    // Outlier rejection — same shape as EmaSmoother but applied before the
    // adaptive filter so a single bad frame doesn't spike the derivative.
    const std = Math.sqrt(this.variance);
    let x = raw;
    if (std > 0 && Math.abs(raw - this.xHat) > this.outlierSigma * std) {
      x = this.xHat * 0.75 + raw * 0.25;
    }

    // 1. Estimate the derivative and smooth it
    const dx = (x - this.xHat) / dt;
    const aD = alpha(this.dCutoff, dt);
    this.dxHat = this.dxHat + aD * (dx - this.dxHat);

    // 2. Compute adaptive cutoff for x based on derivative magnitude
    const cutoff = this.minCutoff + this.beta * Math.abs(this.dxHat);
    const aX = alpha(cutoff, dt);

    // 3. Smooth x
    const next = this.xHat + aX * (x - this.xHat);

    // 4. Update variance for outlier check next frame
    const delta = x - this.xHat;
    this.variance =
      this.variance * (1 - aX) + delta * delta * aX;

    this.xHat = next;
    return this.xHat;
  }

  get current(): number | null {
    return this.xHat;
  }

  reset(): void {
    this.xHat = null;
    this.dxHat = 0;
    this.lastT = null;
    this.variance = 0;
  }
}
